import React, { useCallback, useState, useRef, forwardRef, useImperativeHandle } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  IconButton,
  Paper,
  Divider
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'

const useStyles = makeStyles((theme) => ({
  container: {
  },
  accordion: {
    marginBottom: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.divider,
    '&:before': {
      display: 'none'
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50]
  },
  tableContainer: {
    maxHeight: 400,
    overflow: 'auto'
  },
  table: {
    minWidth: 800
  },
  cell: {
    padding: theme.spacing(1),
    minWidth: 100
  },
  smallCell: {
    padding: theme.spacing(0.5),
    minWidth: 60
  },
  textField: {
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.5, 1),
      fontSize: '0.875rem'
    }
  },
  checkbox: {
    padding: theme.spacing(0.5)
  },
  addButton: {
    margin: theme.spacing(1, 0)
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  summary: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.info.contrastText
  }
}))

const ApiOutputsEditTable = forwardRef(({ initialOutputs = [] }, ref) => {
  const classes = useStyles()
  const [outputs, setOutputs] = useState(() =>
    initialOutputs.map((output, index) => ({
      ...output,
      fields: (output.fields || []).map((field, fIdx) => ({
        ...field,
        tempId: field.catalogApiOutputFieldId || `field_temp_${index}_${fIdx}`
      })),
      tempId: output.catalogApiOutputId || `temp_${index}`,
      isNew: !output.catalogApiOutputId
    }))
  )

  useImperativeHandle(ref, () => ({
    getData: () => outputs.map(output => {
      const { tempId, isNew, modified, ...data } = output
      data.fields = (data.fields || []).map(field => {
        const { tempId: fieldTempId, isNew: fieldIsNew, modified: fieldModified, ...fieldData } = field
        return fieldData
      })
      return data
    }),
    getChanges: () => ({
      added: outputs.filter(output => output.isNew).length,
      modified: outputs.filter(output => !output.isNew && output.modified).length
    })
  }))

  const updateOutput = useCallback((tempId, field, value) => {
    setOutputs(prev => prev.map(output =>
      output.tempId === tempId
        ? { ...output, [field]: value, modified: !output.isNew || output.modified }
        : output
    ))
  }, [])

  const deleteOutput = useCallback((tempId) => {
    setOutputs(prev => prev.filter(output => output.tempId !== tempId))
  }, [])

  const addOutput = useCallback(() => {
    const newOutput = {
      tempId: `temp_${Date.now()}`,
      isNew: true,
      modified: false,
      catalogApiOutputId: null,
      catalogDatasourceId: outputs[0]?.catalogDatasourceId || null,
      outputName: '',
      displayName: '',
      schemaDefinition: {},
      description: null,
      isDefault: false,
      fields: []
    }
    setOutputs(prev => [...prev, newOutput])
  }, [outputs])

  const addField = useCallback((outputTempId) => {
    setOutputs(prev => prev.map(output => {
      if (output.tempId === outputTempId) {
        const newField = {
          tempId: `field_temp_${Date.now()}`,
          isNew: true,
          catalogApiOutputFieldId: null,
          catalogDatasourceId: output.catalogDatasourceId,
          catalogApiOutputId: output.catalogApiOutputId,
          path: '',
          displayName: '',
          dataType: 'string',
          enumerationId: null,
          isNullable: true,
          isPii: false,
          isAiEnabled: false,
          description: null,
          ordinal: Math.max(...output.fields.map(f => f.ordinal || 0), 0) + 1
        }
        return {
          ...output,
          fields: [...output.fields, newField],
          modified: true
        }
      }
      return output
    }))
  }, [])

  const deleteField = useCallback((outputTempId, fieldTempId) => {
    setOutputs(prev => prev.map(output => {
      if (output.tempId === outputTempId) {
        return {
          ...output,
          fields: output.fields.filter(f => f.tempId !== fieldTempId),
          modified: true
        }
      }
      return output
    }))
  }, [])

  const updateField = useCallback((outputTempId, fieldTempId, field, value) => {
    setOutputs(prev => prev.map(output => {
      if (output.tempId === outputTempId) {
        return {
          ...output,
          fields: output.fields.map(f =>
            f.tempId === fieldTempId
              ? { ...f, [field]: value, modified: true }
              : f
          ),
          modified: true
        }
      }
      return output
    }))
  }, [])

  const handleTextFieldChange = useCallback((tempId, field) => (event) => {
    const value = event.target.value
    updateOutput(tempId, field, value === '' ? null : value)
  }, [updateOutput])

  const handleCheckboxChange = useCallback((tempId, field) => (event) => {
    updateOutput(tempId, field, event.target.checked)
  }, [updateOutput])

  const handleFieldTextChange = useCallback((outputTempId, fieldTempId, field) => (event) => {
    const value = event.target.value
    updateField(outputTempId, fieldTempId, field, value === '' ? null : value)
  }, [updateField])

  const handleFieldCheckboxChange = useCallback((outputTempId, fieldTempId, field) => (event) => {
    updateField(outputTempId, fieldTempId, field, event.target.checked)
  }, [updateField])

  const handleFieldNumberChange = useCallback((outputTempId, fieldTempId, field) => (event) => {
    const value = event.target.value
    const numValue = value === '' ? null : parseInt(value, 10)
    updateField(outputTempId, fieldTempId, field, isNaN(numValue) ? null : numValue)
  }, [updateField])

  return (
    <div>
      <div className={classes.addButton}>
        <IconButton
          color='primary'
          onClick={addOutput}
          size='small'
        >
          <AddIcon />
        </IconButton>
        <Typography variant='body2' component='span' style={{ marginLeft: 8 }}>
          Add Output
        </Typography>
      </div>

      {outputs.map((output) => (
        <Accordion key={output.tempId} className={classes.accordion} defaultExpanded={output.isNew}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation()
                  deleteOutput(output.tempId)
                }}
                color='secondary'
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
              <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
                {output.outputName || '(unnamed output)'}
              </Typography>
              {output.isDefault && (
                <span
                  style={{
                    backgroundColor: 'var(--color-accent-500)',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  DEFAULT
                </span>
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ width: '100%' }}>
              <Grid container spacing={2}>
                {/* Output Properties */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label='Output Name'
                    value={output.outputName || ''}
                    onChange={handleTextFieldChange(output.tempId, 'outputName')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    placeholder='result'
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label='Display Name'
                    value={output.displayName || ''}
                    onChange={handleTextFieldChange(output.tempId, 'displayName')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    placeholder='Result'
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Checkbox
                      checked={!!output.isDefault}
                      onChange={handleCheckboxChange(output.tempId, 'isDefault')}
                      color='primary'
                    />
                    <Typography variant='body2'>Is Default</Typography>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Description'
                    value={output.description || ''}
                    onChange={handleTextFieldChange(output.tempId, 'description')}
                    variant='outlined'
                    size='small'
                    fullWidth
                    multiline
                    rows={2}
                    placeholder='Describe this output structure'
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider style={{ margin: '16px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <Typography variant='subtitle2' style={{ fontWeight: 600 }}>
                      Output Fields ({output.fields.length})
                    </Typography>
                    <IconButton
                      color='primary'
                      onClick={() => addField(output.tempId)}
                      size='small'
                    >
                      <AddIcon />
                    </IconButton>
                  </div>

                  {output.fields.length > 0 ? (
                    <TableContainer component={Paper} className={classes.tableContainer}>
                      <Table size='small' stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell className={classes.headerCell}>Actions</TableCell>
                            <TableCell className={classes.headerCell}>Path</TableCell>
                            <TableCell className={classes.headerCell}>Display Name</TableCell>
                            <TableCell className={classes.headerCell}>Data Type</TableCell>
                            <TableCell className={classes.headerCell}>Nullable</TableCell>
                            <TableCell className={classes.headerCell}>PII</TableCell>
                            <TableCell className={classes.headerCell}>AI</TableCell>
                            <TableCell className={classes.headerCell}>Ordinal</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {output.fields.map((field) => (
                            <TableRow key={field.tempId}>
                              <TableCell className={classes.smallCell}>
                                <IconButton
                                  size='small'
                                  onClick={() => deleteField(output.tempId, field.tempId)}
                                  color='secondary'
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </TableCell>

                              <TableCell className={classes.cell}>
                                <TextField
                                  value={field.path || ''}
                                  onChange={handleFieldTextChange(output.tempId, field.tempId, 'path')}
                                  variant='outlined'
                                  size='small'
                                  fullWidth
                                  className={classes.textField}
                                  placeholder='response.data.id'
                                />
                              </TableCell>

                              <TableCell className={classes.cell}>
                                <TextField
                                  value={field.displayName || ''}
                                  onChange={handleFieldTextChange(output.tempId, field.tempId, 'displayName')}
                                  variant='outlined'
                                  size='small'
                                  fullWidth
                                  className={classes.textField}
                                  placeholder='Display Name'
                                />
                              </TableCell>

                              <TableCell className={classes.cell}>
                                <TextField
                                  value={field.dataType || ''}
                                  onChange={handleFieldTextChange(output.tempId, field.tempId, 'dataType')}
                                  variant='outlined'
                                  size='small'
                                  fullWidth
                                  className={classes.textField}
                                  placeholder='string'
                                />
                              </TableCell>

                              <TableCell className={classes.smallCell}>
                                <Checkbox
                                  checked={!!field.isNullable}
                                  onChange={handleFieldCheckboxChange(output.tempId, field.tempId, 'isNullable')}
                                  className={classes.checkbox}
                                  color='primary'
                                />
                              </TableCell>

                              <TableCell className={classes.smallCell}>
                                <Checkbox
                                  checked={!!field.isPii}
                                  onChange={handleFieldCheckboxChange(output.tempId, field.tempId, 'isPii')}
                                  className={classes.checkbox}
                                  color='primary'
                                />
                              </TableCell>

                              <TableCell className={classes.smallCell}>
                                <Checkbox
                                  checked={!!field.isAiEnabled}
                                  onChange={handleFieldCheckboxChange(output.tempId, field.tempId, 'isAiEnabled')}
                                  className={classes.checkbox}
                                  color='primary'
                                />
                              </TableCell>

                              <TableCell className={classes.smallCell}>
                                <TextField
                                  value={field.ordinal || ''}
                                  onChange={handleFieldNumberChange(output.tempId, field.tempId, 'ordinal')}
                                  variant='outlined'
                                  size='small'
                                  type='number'
                                  className={classes.textField}
                                  required
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant='body2' style={{ color: 'var(--text-color-subtle)', padding: '16px' }}>
                      No fields defined. Click + to add fields.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </div>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  )
})

ApiOutputsEditTable.propTypes = {
  initialOutputs: PropTypes.array
}

ApiOutputsEditTable.displayName = 'ApiOutputsEditTable'

const useSubmitter = (dataSource, outputsRef, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyApiOutputs } = dataCatalog.useModifyDatasourceApiOutputs()

  const onSubmit = useCallback(async () => {
    if (!outputsRef.current) {
      setError('Unable to access outputs table')
      return
    }

    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      apiOutputs: outputsRef.current.getData()
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyApiOutputs(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the API outputs')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, outputsRef, onComplete, modifyApiOutputs])

  return {
    onSubmit,
    processing,
    error
  }
}

function EditApiOutputsForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const outputsRef = useRef()

  const { onSubmit, processing, error } = useSubmitter(dataSource, outputsRef, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ApiOutputsEditTable
            ref={outputsRef}
            initialOutputs={dataSource.apiOutputs || []}
          />
        </Grid>

        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}

            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={onSubmit}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

EditApiOutputsForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    apiOutputs: PropTypes.array
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default EditApiOutputsForm
